/**
 * SKILL.md files, read and written in the browser, for Settings → Skills'
 * import and export.
 *
 * The front matter's `name` and `description` are the skill's and the body is
 * its instructions; `disable-model-invocation: true` is automatic use off.
 * Every other key is left out, `allowed-tools` among them, and so is anything
 * bundled beside the file. The front matter is read as far as SKILL.md files
 * use YAML — a value on its line, quoted, or a `|` or `>` block — and no
 * further. An imported skill arrives with automatic use off whatever the file
 * says; that is the import's rule, not this reader's.
 */

import type { Skill } from './api';

export type SkillFields = Pick<Skill, 'name' | 'description' | 'instructions' | 'auto'>;

/** YAML folding: lines joined by spaces, a blank line kept as a break. */
const fold = (lines: string[]): string =>
  lines.reduce((out, l) => (!l ? `${out}\n` : !out || out.endsWith('\n') ? out + l : `${out} ${l}`), '');

/** One key's value: `first` is what follows the colon, `more` the indented
 *  lines under it. */
function value(first: string, more: string[]): string {
  const block = /^([|>])[+-]?\d*\s*(?:#.*)?$/.exec(first);
  if (block) {
    const indent = Math.min(...more.filter((l) => l.trim()).map((l) => l.length - l.trimStart().length));
    const lines = more.map((l) => l.slice(indent).trimEnd());
    return (block[1] === '|' ? lines.join('\n') : fold(lines)).trim();
  }
  const joined = fold([first, ...more.map((l) => l.trim())]);
  const double = /^"((?:[^"\\]|\\.)*)"/.exec(joined);
  if (double) {
    try {
      return JSON.parse(`"${double[1]}"`) as string;
    } catch {
      return double[1]!;
    }
  }
  const single = /^'((?:[^']|'')*)'/.exec(joined);
  if (single) return single[1]!.replace(/''/g, "'");
  return joined.replace(/(^|\s)#.*$/, '').trim();
}

/** What a SKILL.md holds. A field it does not have comes back empty, for the
 *  person to fill in before it is added. */
export function readSkillFile(text: string): SkillFields {
  const lines = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n').split('\n');
  const end = lines[0]?.trim() === '---' ? lines.findIndex((l, i) => i > 0 && /^(---|\.\.\.)\s*$/.test(l)) : -1;
  const fields = new Map<string, string>();
  for (let i = 1; i < end; i++) {
    const m = /^([A-Za-z0-9_-]+)\s*:\s*(.*)$/.exec(lines[i]!);
    if (!m) continue;
    const more: string[] = [];
    while (i + 1 < end && (!lines[i + 1]!.trim() || /^\s/.test(lines[i + 1]!))) more.push(lines[++i]!);
    fields.set(m[1]!.toLowerCase(), value(m[2]!.trim(), more));
  }
  return {
    name: (fields.get('name') ?? '').toLowerCase(),
    description: fields.get('description') ?? '',
    instructions: (end > 0 ? lines.slice(end + 1) : lines).join('\n').trim(),
    auto: (fields.get('disable-model-invocation') ?? '').toLowerCase() !== 'true'
  };
}

/** A skill as a SKILL.md. The description is always quoted, since a colon or
 *  a `#` in it would otherwise change what it says. */
export function skillFile(skill: SkillFields): string {
  return [
    '---',
    `name: ${skill.name}`,
    `description: ${JSON.stringify(skill.description)}`,
    ...(skill.auto ? [] : ['disable-model-invocation: true']),
    '---',
    '',
    skill.instructions,
    ''
  ].join('\n');
}
